import React, { Suspense, useEffect, useRef, useState } from 'react';

// The globe sits below the fold (WU-8): its code is a separate chunk, kept out of the first load.
// It mounts once the section is within 300 px of the viewport, or once the page is idle after load,
// whichever comes first, so a keyboard user tabbing down the page always finds it in the tab order.
// The placeholder has the globe's size, so nothing moves when it arrives.
const HeroGlobe = React.lazy(() => import('./HeroGlobe'));

const NEAR_VIEWPORT = '300px';
const IDLE_TIMEOUT_MS = 2000;

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

const LazyGlobe: React.FC = () => {
  const holderRef = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const holder = holderRef.current;
    if (holder == null || typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: NEAR_VIEWPORT },
    );
    observer.observe(holder);

    const win = window as IdleWindow;
    let idleHandle: number | null = null;
    let timer: number | null = null;
    const whenIdle = () => {
      if (win.requestIdleCallback) {
        idleHandle = win.requestIdleCallback(() => setNear(true), { timeout: IDLE_TIMEOUT_MS });
      } else {
        timer = window.setTimeout(() => setNear(true), IDLE_TIMEOUT_MS);
      }
    };
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('load', whenIdle);
      if (idleHandle != null) win.cancelIdleCallback?.(idleHandle);
      if (timer != null) window.clearTimeout(timer);
    };
  }, []);

  const placeholder = <div className="lazy-globe__placeholder" aria-hidden="true" />;

  return (
    <div ref={holderRef} className="lazy-globe">
      {near ? (
        <Suspense fallback={placeholder}>
          <HeroGlobe />
        </Suspense>
      ) : (
        placeholder
      )}
    </div>
  );
};

export default LazyGlobe;
